import type React from "react";
import { useCallback, useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { toast } from "react-toastify";
import { CostAnalysisDetail } from "./types/cost-analysis";
import apiService from "@/service/Api";
import { addCrewPayment } from "./types/cost-analysis-api";

// NOTE: reuses the same /api/call-sheets/callsheet/?stream= endpoint
// AssignCrewModal already calls - not duplicating any assignment logic,
// just reading who's already on this stream's callsheet.
interface CallSheetRole {
  id: number;
  name?: string;
}

interface CallSheetAssignment {
  id: number;
  crew_member: {
    id: number;
    user: {
      id: number;
      first_name: string;
      last_name: string;
      email: string;
    };
  };
  roles: Array<number | CallSheetRole>;
}

interface RosterCrewOption {
  crewMemberId: number;
  name: string;
  roles: { id: number; name: string }[];
}

interface CrewPaymentModalProps {
  open: boolean;
  onClose: () => void;
  costAnalysisId: number | string;
  streamId: number | string;
  onPaid: (updated: CostAnalysisDetail) => void;
}

const CrewPaymentModal: React.FC<CrewPaymentModalProps> = ({
  open,
  onClose,
  costAnalysisId,
  streamId,
  onPaid,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [roster, setRoster] = useState<RosterCrewOption[]>([]);

  const [selectedCrewMemberId, setSelectedCrewMemberId] = useState<number | "">(
    "",
  );
  const [selectedRoleId, setSelectedRoleId] = useState<number | "">("");
  const [submitting, setSubmitting] = useState(false);

  const fetchRoster = useCallback(async () => {
    if (!streamId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await apiService.get<{ results: CallSheetAssignment[] }>(
        `/api/call-sheets/callsheet/?stream=${streamId}`,
      );
      const rows = res.data.results || [];

      // Collapse multiple CallSheet rows for the same person into one
      // roster entry with all their roles on this stream.
      const byCrewMember = new Map<number, RosterCrewOption>();
      for (const row of rows) {
        const crewMemberId = row.crew_member.id;
        const name =
          `${row.crew_member.user.first_name} ${row.crew_member.user.last_name}`.trim() ||
          row.crew_member.user.email ||
          `Crew #${crewMemberId}`;

        const roleObjs = row.roles.map((r) =>
          typeof r === "number"
            ? { id: r, name: `Role #${r}` }
            : { id: r.id, name: r.name || `Role #${r.id}` },
        );

        const existing = byCrewMember.get(crewMemberId);
        if (existing) {
          const existingIds = new Set(existing.roles.map((r) => r.id));
          for (const r of roleObjs) {
            if (!existingIds.has(r.id)) existing.roles.push(r);
          }
        } else {
          byCrewMember.set(crewMemberId, {
            crewMemberId,
            name,
            roles: roleObjs,
          });
        }
      }

      setRoster(Array.from(byCrewMember.values()));
    } catch (err) {
      console.error("Failed to load callsheet roster:", err);
      setError("Failed to load the crew roster for this stream.");
    } finally {
      setLoading(false);
    }
  }, [streamId]);

  useEffect(() => {
    if (open) {
      fetchRoster();
      setSelectedCrewMemberId("");
      setSelectedRoleId("");
    }
  }, [open, fetchRoster]);

  const selectedCrewMember = roster.find(
    (r) => r.crewMemberId === selectedCrewMemberId,
  );

  const handleConfirm = async () => {
    if (!selectedCrewMemberId || !selectedRoleId) {
      toast.error("Select a crew member and a role first");
      return;
    }
    setSubmitting(true);
    try {
      const updated = await addCrewPayment(costAnalysisId, {
        crew_member_id: selectedCrewMemberId,
        role_id: selectedRoleId,
      });
      toast.success("Crew payment added");
      onPaid(updated);
      onClose();
    } catch (err) {
      console.error("Failed to add crew payment:", err);
      toast.error("Failed to add crew payment");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h6" fontWeight="bold">
          Pay Crew Member
        </Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
          Choose someone already on this streams callsheet to add a manual crew
          payment (e.g. a company employee who should still be paid).
        </Typography>

        {loading ? (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress size={24} />
          </Box>
        ) : error ? (
          <Alert
            severity="error"
            action={<Button onClick={fetchRoster}>Retry</Button>}
          >
            {error}
          </Alert>
        ) : roster.length === 0 ? (
          <Alert severity="info">
            No crew members are on this streams callsheet yet.
          </Alert>
        ) : (
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <FormControl fullWidth size="small">
              <InputLabel id="crew-payment-member-label">
                Crew Member
              </InputLabel>
              <Select
                labelId="crew-payment-member-label"
                label="Crew Member"
                value={selectedCrewMemberId}
                onChange={(e) => {
                  setSelectedCrewMemberId(e.target.value as number);
                  setSelectedRoleId("");
                }}
              >
                {roster.map((r) => (
                  <MenuItem key={r.crewMemberId} value={r.crewMemberId}>
                    {r.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth size="small" disabled={!selectedCrewMember}>
              <InputLabel id="crew-payment-role-label">Role</InputLabel>
              <Select
                labelId="crew-payment-role-label"
                label="Role"
                value={selectedRoleId}
                onChange={(e) => setSelectedRoleId(e.target.value as number)}
              >
                {(selectedCrewMember?.roles || []).map((role) => (
                  <MenuItem key={role.id} value={role.id}>
                    {role.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!selectedCrewMemberId || !selectedRoleId || submitting}
          onClick={handleConfirm}
        >
          {submitting ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            "Add Payment"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CrewPaymentModal;
